import { Link, useLocation, useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import {
  ArrowLeftIcon,
  UsersIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  EnvelopeIcon,
  KeyIcon,
  WrenchScrewdriverIcon,
  BuildingOfficeIcon,
  RectangleStackIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';

// ── Tab definitions per role ──────────────────────────────────────────
const ADMIN_TABS = [
  { id: 'users',    label: 'Users',              icon: UsersIcon,             path: '/admin/settings/users' },
  { id: 'roles',    label: 'Roles & Permissions', icon: ShieldCheckIcon,      path: '/admin/settings/roles' },
  { id: 'teams',    label: 'Teams',               icon: UserGroupIcon,        path: '/admin/settings/teams' },
  { id: 'system',   label: 'System',              icon: Cog6ToothIcon,        path: '/admin/settings/system' },
];

const ENTERPRISE_ADMIN_TABS = [
  { id: 'org-users',     label: 'Organization Users', icon: UsersIcon,           path: '/admin/settings/org-users' },
  { id: 'custom-fields', label: 'Custom Fields',      icon: RectangleStackIcon,  path: '/admin/settings/custom-fields' },
  { id: 'org-settings',  label: 'Org Settings',       icon: BuildingOfficeIcon,  path: '/admin/settings/org-settings' },
];

const USER_TABS = [
  { id: 'profile',  label: 'My Profile',  icon: UserCircleIcon, path: '/user/settings/profile' },
  { id: 'system',   label: 'System',      icon: Cog6ToothIcon,  path: '/user/settings/system' },
];

export default function SettingsSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const isAdmin = user?.is_superuser;
  const isEnterpriseAdmin = user?.profile?.account_type === 'enterprise_admin';

  // Determine which tabs to show
  let tabs = USER_TABS;
  if (isAdmin) {
    tabs = ADMIN_TABS;
  } else if (isEnterpriseAdmin) {
    tabs = ENTERPRISE_ADMIN_TABS;
  }

  const isActive = (path) => location.pathname === path;

  return (
    <aside className="w-[260px] flex-shrink-0 h-full bg-theme-secondary border-r border-theme-primary flex flex-col transition-colors duration-300">
      {/* ── Back to Dashboard ── */}
      <div className="p-4 border-b border-theme-primary">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2.5 px-3 py-2.5 w-full rounded-xl text-theme-secondary hover:text-theme-primary hover:bg-theme-tertiary transition-all duration-200"
        >
          <ArrowLeftIcon className="w-5 h-5 flex-shrink-0" />
          <span className="font-medium text-sm">Back to Dashboard</span>
        </button>
      </div>

      {/* ── Settings Header ── */}
      <div className="px-6 py-4">
        <h2 className="text-lg font-bold text-theme-primary">Settings</h2>
        <p className="text-xs text-theme-muted mt-0.5">
          {isAdmin ? 'Administration' : isEnterpriseAdmin ? 'Organization' : 'Account'}
        </p>
      </div>

      {/* ── Navigation Tabs ── */}
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {tabs.map((tab) => {
          const active = isActive(tab.path);
          return (
            <Link
              key={tab.id}
              to={tab.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 relative ${
                active
                  ? 'bg-gradient-to-r from-brand-orange/20 to-brand-amber/10 text-brand-orange border border-brand-orange/30'
                  : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-tertiary'
              }`}
            >
              {active && (
                <span className="absolute left-0 top-2 bottom-2 w-[3px] bg-brand-orange rounded-r" />
              )}
              <tab.icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-brand-orange' : ''}`} />
              <span className="font-medium text-sm">{tab.label}</span>
            </Link>
          );
        })}

        {/* Enterprise admin also gets admin tabs if they're superuser */}
        {isAdmin && isEnterpriseAdmin && (
          <>
            <div className="pt-4 mt-4 border-t border-theme-primary">
              <p className="px-3 mb-2 text-xs font-semibold text-theme-muted uppercase tracking-wider">Organization</p>
            </div>
            {ENTERPRISE_ADMIN_TABS.map((tab) => {
              const active = isActive(tab.path);
              return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 relative ${
                    active
                      ? 'bg-gradient-to-r from-brand-orange/20 to-brand-amber/10 text-brand-orange border border-brand-orange/30'
                      : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-tertiary'
                  }`}
                >
                  {active && (
                    <span className="absolute left-0 top-2 bottom-2 w-[3px] bg-brand-orange rounded-r" />
                  )}
                  <tab.icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-brand-orange' : ''}`} />
                  <span className="font-medium text-sm">{tab.label}</span>
                </Link>
              );
            })}
          </>
        )}
      </nav>

    </aside>
  );
}
